import {
  Calendar,
  CalendarClearDate,
  CalendarSelectDate,
  CalendarTomorrow,
  Sunrise,
} from '@griply/assets';
import { Task, Timestamp, UpdateTask } from '@griply/types';
import { getWeekStartsOn } from '@griply/utils';
import { addDays, endOfDay, isSameDay } from 'date-fns';
import {
  buildDateUpdateTasks,
  getSharedDateValue,
  useUpdateTasks,
} from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { QuickActionRow } from 'shared/components/ui/quick-action-row';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getNextWeekStart } from 'shared/utils/get-next-week-start';

export type TaskDateQuickActionsProps = {
  tasks: Task[];
  onSelectDate: () => void;
  onClose: () => void;
  onAction?: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

export const TaskDateQuickActions: React.FC<TaskDateQuickActionsProps> = ({
  tasks,
  onSelectDate,
  onClose,
  onAction,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { submit: updateTasks } = useUpdateTasks();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const dateValue = useMemo(
    () => getSharedDateValue(tasks, today),
    [tasks, today],
  );

  const todayRef = useRef<HTMLButtonElement>(null);
  const tomorrowRef = useRef<HTMLButtonElement>(null);
  const nextWeekRef = useRef<HTMLButtonElement>(null);
  const selectDateRef = useRef<HTMLButtonElement>(null);
  const clearDateRef = useRef<HTMLButtonElement>(null);

  const applyDate = (date?: Timestamp) => {
    const updates = buildDateUpdateTasks(tasks, date, dateValue, {
      weekStartsOn,
      today,
    });

    if (!updates.length) {
      onClose();
      return;
    }

    onUpdate
      ? updates.forEach((update) => {
          const updatedTask = tasks.find(({ id }) => id === update.id);
          if (updatedTask) {
            onUpdate(updatedTask, update);
          }
        })
      : updateTasks(updates);

    onAction?.();
    onClose();
  };

  const setDateToday = () => applyDate(endOfDay(new Date()));
  const setDateTomorrow = () => applyDate(endOfDay(addDays(new Date(), 1)));
  const setDateNextWeek = () =>
    applyDate(endOfDay(getNextWeekStart(new Date(), weekStartsOn)));
  const clearDate = () => applyDate(undefined);

  const isDateToday = !!dateValue && isSameDay(dateValue, new Date());
  const isDateTomorrow =
    !!dateValue && isSameDay(dateValue, addDays(new Date(), 1));
  const isDateNextWeek =
    !!dateValue &&
    isSameDay(dateValue, getNextWeekStart(new Date(), weekStartsOn));

  return (
    <QuickActionRow
      label={t('context-menus.task.multi-select.options.date.label')}
      shortcut="D"
    >
      <IconButton
        ref={todayRef}
        icon={Calendar}
        onClick={setDateToday}
        color={Colors.Success}
        highlighted={isDateToday}
        aria-label={t('forms.deadline.fields.today.label')}
      />
      <Tooltip
        referenceElement={todayRef}
        label={t('forms.deadline.fields.today.label')}
      />
      <IconButton
        ref={tomorrowRef}
        icon={Sunrise}
        onClick={setDateTomorrow}
        color={Colors.Info}
        highlighted={isDateTomorrow}
        aria-label={t('forms.deadline.fields.tomorrow.label')}
      />
      <Tooltip
        referenceElement={tomorrowRef}
        label={t('forms.deadline.fields.tomorrow.label')}
      />
      <IconButton
        ref={nextWeekRef}
        icon={CalendarTomorrow}
        onClick={setDateNextWeek}
        color={Colors.Warning}
        highlighted={isDateNextWeek}
        aria-label={t('forms.deadline.fields.next-week.label')}
      />
      <Tooltip
        referenceElement={nextWeekRef}
        label={t('forms.deadline.fields.next-week.label')}
      />
      <IconButton
        ref={selectDateRef}
        icon={CalendarSelectDate}
        onClick={onSelectDate}
        color={Colors.Inherit}
        aria-label={t('forms.deadline.fields.select-date.label')}
      />
      <Tooltip
        referenceElement={selectDateRef}
        label={t('forms.deadline.fields.select-date.label')}
      />
      {!!dateValue && (
        <>
          <IconButton
            ref={clearDateRef}
            icon={CalendarClearDate}
            onClick={clearDate}
            color={Colors.Default}
            aria-label={t('forms.deadline.fields.clear-date.label')}
          />
          <Tooltip
            referenceElement={clearDateRef}
            label={t('forms.deadline.fields.clear-date.label')}
          />
        </>
      )}
    </QuickActionRow>
  );
};
